/** Desktop-only WC3 transport. The native recorder supplies the sole eligible process. */
export interface BnetMatchContext {
    matchId: string; contextId: string; processId: number; processCreatedAt: string; guid: string; ports: number[];
}
export interface BnetStatsDiagnostic {
    playerId?: string; stage: 'setup' | 'player'; attempt: number;
    outcome: 'ready' | 'empty' | 'retry' | 'unavailable' | 'cache'; reason?: string; durationMs: number; records?: number; rejections?: string[];
}
export interface BnetWireDiagnostic {
    kind: 'bnet-websocket'; version: 1; traceId: string; sequence: number; timestamp: number;
    matchId: string; connectionId: number; event: string; requestId?: number;
    pendingRequestId?: number; command?: string; pendingCommand?: string;
    durationMs?: number; reason?: string; code?: number; stale?: boolean;
    raw?: string; originalChars?: number;
}
export interface BnetStatsUpdate { matchId: string; contextId: string; playerId: string; battleTag: string; fetchedAt: number; observedAt?: number; source?: 'live' | 'cache'; records: any[] }
const tag = (value: any) => typeof value === 'string' ? value.toLowerCase() : '';
const nativeName = (value: any) => { try { return typeof value === 'string' ? decodeURIComponent(value) : ''; } catch { return ''; } };
const finite = (value: any) => typeof value === 'number' && Number.isFinite(value);
const mode = (value: string) => value === 'sffa' ? 'ffa' : value;
const CACHE_MAX_AGE_MS = 48 * 60 * 60 * 1000;
const races = new Set(['human', 'orc', 'undead', 'night_elf', 'random']);

export interface BnetRosterDiagnostic {
    kind: 'roster-comparison'; attempt: number; gameType: string; issues: string[];
    expectedCount: number; receivedCount: number;
    expected: { slot: string; battleTag: string; eligible: boolean }[];
    received: { slot: string; battleTag: string; gateway?: string | number }[];
}

/** The loading-screen roster identifies each player's gateway, including cross-region games. */
export function compareBnetRoster(players: any[], gameData: any): { gateways: Map<string, number> | null; issues: string[] } {
    const issues: string[] = [];
    if (!gameData || typeof gameData !== 'object') issues.push('missing-game-data');
    if (gameData?.gameType !== 'versus') issues.push('not-versus');
    if (!Array.isArray(gameData?.teams)) issues.push('missing-teams');
    const teams = Array.isArray(gameData?.teams) ? gameData.teams : [];
    if (teams.some(t => !Array.isArray(t?.players))) issues.push('missing-team-players');
    const roster = teams.flatMap(t => Array.isArray(t?.players) ? t.players : []);
    if (!players.length) issues.push('no-eligible-native-players');
    if (roster.length !== players.length) issues.push('player-count-mismatch');
    const result = new Map<string, number>();
    for (const player of players) {
        const slot = String(player.id);
        const matches = roster.filter(p => p && tag(p.battletag) === tag(player.name) && String(p.playerId) === slot);
        if (!matches.length) {
            const byTag = roster.filter(p => p && tag(p.battletag) === tag(player.name));
            const bySlot = roster.filter(p => p && String(p.playerId) === slot);
            issues.push((byTag.length ? 'slot-mismatch:' : bySlot.length ? 'battletag-mismatch:' : 'missing-player:') + slot);
        } else if (matches.length !== 1) issues.push('duplicate-player:' + slot);
        else if (![10, 20, 30].includes(matches[0].gatewayId)) issues.push('invalid-gateway:' + slot);
        else result.set(slot, matches[0].gatewayId);
    }
    if (result.size !== players.length && !issues.length) issues.push('duplicate-native-slot');
    return { gateways: issues.length ? null : result, issues };
}
export function bnetMatchGateways(players: any[], gameData: any): Map<string, number> | null {
    return compareBnetRoster(players, gameData).gateways;
}

export function normalizeBnetStats(profile: any, batch: any, battleTag: string, divisions: any[] = [], rejected: (reason: string) => void = () => { }): any[] {
    if (tag(profile?.battle_tag_full) !== tag(battleTag) || !Number.isInteger(profile.matchmaked_current_season)) { rejected('profile-identity-or-season'); return []; }
    const season = profile.matchmaked_current_season;
    const result = [];
    const stats = (raw: any, mmr: number) => {
        if (![raw.wins, raw.losses, mmr].every(finite) || !Number.isSafeInteger(raw.wins) || !Number.isSafeInteger(raw.losses) || raw.wins < 0 || raw.losses < 0) return null;
        return {
            mmr, wins: raw.wins, losses: raw.losses, winRate: raw.wins + raw.losses ? 100 * raw.wins / (raw.wins + raw.losses) : 0,
            ...(finite(raw.rank) && raw.rank > 0 ? { rank: raw.rank } : {}), ...(finite(raw.xp) ? { xp: raw.xp } : {})
        };
    };
    for (const raw of batch.mm?.soloStats || []) {
        if (!raw || raw.seasonId !== season || !races.has(raw.race) || !['1v1', '2v2', '3v3', '4v4', 'sffa'].includes(raw.gameMode)) { rejected('record-season-race-mode'); continue; }
        // MMStats has no identity. Require the adjacent history batch to corroborate
        // this target, mode, season and race; empty/ambiguous history is unavailable.
        const matches = batch.history?.[raw.gameMode]?.matches?.filter((m: any) => !m.arranged && m.ranked && m.season === season);
        if (!matches?.length) { rejected('missing-ranked-history'); continue; }
        if (!matches.every((m: any) => m.teamMembers?.some((p: any) => tag(p.battleTag) === tag(battleTag) && p.gatewayId === profile.requested_gateway_id)) ||
            !matches.some((m: any) => m.teamMembers.some((p: any) => tag(p.battleTag) === tag(battleTag) && p.race === raw.race))) { rejected('history-identity-gateway-race'); continue; }
        // The identity-bearing profile also corroborates counts when supplied;
        // a cached MMStats event from another gateway must not pass on history alone.
        if (Array.isArray(profile.matchmaked_stats) && !profile.matchmaked_stats.some((s: any) =>
            s.gateway_id === profile.requested_gateway_id && s.season_id === season && s.game_mode_name === raw.gameMode &&
            s.wins === raw.wins && s.losses === raw.losses)) { rejected('profile-count-mismatch'); continue; }
        const values = stats(raw, raw.mmr); if (!values) { rejected('invalid-record-values'); continue; }
        const division = raw.rankStatus === false ? 0 : divisions.find(d => raw.mmr >= d.lowerBoundMMR && raw.mmr < d.upperBoundMMR)?.divisionId;
        result.push({
            provider: 'bnet', gameMode: mode(raw.gameMode), queue: 'individual', season, race: raw.race === 'night_elf' ? 'night-elf' : raw.race, ...values,
            ...(typeof raw.rankStatus === 'boolean' ? { isPlaced: raw.rankStatus } : {}), ...(Number.isInteger(division) ? { league: division } : {})
        });
    }
    for (const raw of batch.at?.gameTypes || []) {
        if (raw.season !== season || !['2v2', '3v3', '4v4'].includes(raw.gameMode) || typeof raw.teamName !== 'string' || !Array.isArray(raw.members) ||
            !raw.members.some((p: any) => tag(p.name) === tag(battleTag) && p.gatewayId === profile.requested_gateway_id) || raw.members.length < 2 || raw.members.length > Number(raw.gameMode[0]) ||
            !raw.members.every((p: any) => typeof p.name === 'string' && p.name.includes('#') && Number.isInteger(p.gatewayId) && Number.isInteger(p.id))) continue;
        const values = stats(raw, raw.currentMMR); if (!values) { rejected('invalid-record-values'); continue; }
        result.push({
            provider: 'bnet', gameMode: mode(raw.gameMode), queue: 'arranged', season,
            team: { id: raw.teamName, members: raw.members.map((p: any) => ({ id: String(p.id), battleTag: p.name, gatewayId: p.gatewayId })) },
            ...values, ...(Number.isInteger(raw.division) ? { league: raw.division } : {})
        });
    }
    return result;
}

export class BnetStatsFetcher {
    private generation = 0;
    private socket: WebSocket | null = null;
    private pending: { receive: (message: any) => void; reject: (reason: Error) => void } | null = null;
    private activeKey = '';
    private readonly traceId = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
    private wireSequence = 0;
    private connectionSequence = 0;
    private requestSequence = 0;
    private readonly connections = new WeakMap<WebSocket, { matchId: string; connectionId: number }>();
    private pendingWire: { requestId: number; command: string } | null = null;
    private trace(ws: WebSocket, event: string, extra: Partial<BnetWireDiagnostic> = {}): void {
        try {
            if (!this.wire || !this.wireEnabled()) return;
            const connection = this.connections.get(ws);
            if (!connection) return;
            this.wire({ kind: 'bnet-websocket', version: 1, traceId: this.traceId,
                sequence: ++this.wireSequence, timestamp: Date.now(), ...connection, event,
                ...(ws === this.socket && this.pendingWire ? { pendingRequestId: this.pendingWire.requestId, pendingCommand: this.pendingWire.command } : {}), ...extra });
        } catch { /* Capturing diagnostics cannot alter stats handling. */ }
    }
    private send(command: string, payload: any, requestId = ++this.requestSequence): void {
        const raw = JSON.stringify({ type: 'webui', message: command, payload });
        this.trace(this.socket, 'send', { requestId, command, raw, originalChars: raw.length });
        try { this.socket.send(raw); }
        catch (error) { this.trace(this.socket, 'send-error', { requestId, command }); throw error; }
    }
    private cancelDelay: (() => void) | null = null;
    private deadline = Infinity;
    // Session-only, bounded cache. Never contains GUIDs, ports or raw profiles.
    private readonly cache = new Map<string, { records: any[]; observedAt: number }>();
    private seasonInfo: { seasonId: number; divisions: any[]; fetchedAt: number } | null = null;
    private cacheExpiry: ReturnType<typeof setTimeout> | null = null;
    clearCache(): void {
        if (this.cacheExpiry !== null) clearTimeout(this.cacheExpiry);
        this.cacheExpiry = null; this.cache.clear(); this.seasonInfo = null;
    }
    private pruneCache(): void {
        if (this.cacheExpiry !== null) clearTimeout(this.cacheExpiry);
        this.cacheExpiry = null;
        const now = Date.now();
        for (const [key, entry] of this.cache) {
            const age = now - entry.observedAt;
            if (age < 0 || age > CACHE_MAX_AGE_MS) this.cache.delete(key);
        }
        if (this.cache.size) {
            const oldest = Math.min(...[...this.cache.values()].map(entry => entry.observedAt));
            this.cacheExpiry = setTimeout(() => this.pruneCache(), Math.max(1, oldest + CACHE_MAX_AGE_MS + 1 - now));
            // Node regression tests must not be kept alive by browser cache upkeep.
            (this.cacheExpiry as any).unref?.();
        }
    }
    private cacheKey(player: any, gatewayId: number): string { return tag(player.name) + ':' + gatewayId; }
    private remember(player: any, gatewayId: number, records: any[], observedAt: number): void {
        const key = this.cacheKey(player, gatewayId);
        this.cache.delete(key);
        if (!records.length) { this.pruneCache(); return; }
        this.cache.set(key, { records: JSON.parse(JSON.stringify(records)), observedAt });
        while (this.cache.size > 256) this.cache.delete(this.cache.keys().next().value);
        this.pruneCache();
    }

    constructor(private readonly publish: (update: BnetStatsUpdate) => void,
        private readonly connect: (url: string) => WebSocket = url => new WebSocket(url),
        private readonly diagnostic: (event: BnetStatsDiagnostic) => void = () => { },
        private readonly verbose: (event: BnetRosterDiagnostic) => void = () => { },
        private readonly wire?: (event: BnetWireDiagnostic) => void,
        private readonly wireEnabled: () => boolean = () => true) { }

    stop(): void {
        this.generation++; this.activeKey = '';
        this.cancelDelay?.(); this.cancelDelay = null;
        this.pending?.reject(new Error('Match context expired')); this.pending = null;
        this.socket?.close(); this.socket = null;
    }
    start(match: any, context: BnetMatchContext): void {
        if (!match || match.realm !== 'Reforged' || match.isReplay || String(match.id) !== context?.matchId || match.statsContext !== context.contextId ||
            !Number.isInteger(context.processId) || context.processId <= 0 || !/^\d+$/.test(context.processCreatedAt) || !/^\d{11,20}$/.test(context.guid) ||
            !Array.isArray(context.ports) || !context.ports.length || context.ports.length > 16 || context.ports.some(p => !Number.isInteger(p) || p < 1 || p > 65535)) return;
        if (this.activeKey === context.contextId) return;
        this.stop(); this.activeKey = context.contextId; const generation = this.generation;
        void this.run(match, context, generation).catch(() => { /* Missing stats never fail the recorder. */ });
    }
    private report(event: BnetStatsDiagnostic): void {
        try { this.diagnostic(event); } catch { /* Diagnostics must not interrupt recording. */ }
    }
    private async delay(ms: number, generation: number): Promise<void> {
        if (!this.current(generation)) throw new Error('Expired');
        await new Promise<void>((resolve, reject) => {
            const timer = setTimeout(() => { this.cancelDelay = null; resolve(); }, Math.min(ms, Math.max(0, this.deadline - Date.now())));
            this.cancelDelay = () => { clearTimeout(timer); reject(new Error('Expired')); };
        });
        if (!this.current(generation) || Date.now() >= this.deadline) throw new Error('Expired');
    }
    private current(generation: number): boolean { return this.generation === generation; }
    private async open(context: BnetMatchContext, generation: number): Promise<void> {
        for (const port of context.ports) {
            if (!this.current(generation) || Date.now() >= this.deadline) throw new Error('Expired');
            const ws = this.connect(`ws://127.0.0.1:${port}/webui-socket/${context.guid}`);
            this.socket = ws;
            this.connections.set(ws, { matchId: context.matchId, connectionId: ++this.connectionSequence });
            this.trace(ws, 'connecting');
            // Install before open: record every received frame, including unsolicited,
            // malformed and stale broadcasts, before the collector ignores/rejects it.
            ws.onmessage = event => {
                const stale = !this.current(generation) || this.socket !== ws;
                const isText = typeof event.data === 'string';
                const oversized = isText && event.data.length > 2 * 1024 * 1024;
                this.trace(ws, 'receive', { stale, ...(isText ? { originalChars: event.data.length } : {}),
                    ...(isText && !oversized ? { raw: event.data } : { reason: oversized ? 'oversized-frame' : 'non-text-frame' }) });
                if (stale || !isText || oversized) return;
                try { this.pending?.receive(JSON.parse(event.data)); } catch { this.pending?.reject(new Error('Malformed WC3 response')); }
            };
            const opened = await new Promise<boolean>(resolve => {
                const timer = setTimeout(() => { this.trace(ws, 'open-timeout'); ws.close(); resolve(false); }, Math.min(1500, Math.max(0, this.deadline - Date.now())));
                ws.onopen = () => { this.trace(ws, 'open'); clearTimeout(timer); resolve(true); };
                ws.onerror = () => { this.trace(ws, 'error'); clearTimeout(timer); ws.close(); resolve(false); };
                ws.onclose = event => { this.trace(ws, 'close', { code: event?.code }); clearTimeout(timer); resolve(false); if (this.current(generation) && this.socket === ws) this.pending?.reject(new Error('WC3 disconnected')); };
            });
            if (!this.current(generation)) { ws.close(); throw new Error('Expired'); }
            if (!opened) continue;
            return;
        }
        throw new Error('Attached WC3 has no available UI socket');
    }
    private request(command: string, payload: any, receive: (message: any, resolve: (value: any) => void, reject: (reason: Error) => void) => void, timeout = 5000): Promise<any> {
        if (this.pending) return Promise.reject(new Error('Concurrent WC3 request'));
        return new Promise((resolve, reject) => {
            let settled = false;
            const ws = this.socket, requestId = ++this.requestSequence, started = Date.now();
            this.pendingWire = { requestId, command };
            const finish = (error: Error | null, value?: any) => {
                if (settled) return;
                settled = true; clearTimeout(timer);
                this.trace(ws, 'request-end', { requestId, command, durationMs: Date.now() - started, reason: error?.message || 'complete' });
                this.pending = null; this.pendingWire = null;
                error ? reject(error) : resolve(value);
            };
            const timer = setTimeout(() => finish(new Error('WC3 response timed out')), Math.min(timeout, Math.max(0, this.deadline - Date.now())));
            this.pending = { receive: m => receive(m, v => finish(null, v), e => finish(e)), reject: e => finish(e) };
            if (this.socket?.readyState !== 1) { finish(new Error('Socket unavailable')); return; }
            try { this.send(command, payload, requestId); } catch { finish(new Error('Socket send failed')); }
        });
    }
    private playerBatch(player: any, gatewayId: number, arrangedTeams: boolean, onProfile: (profile: any) => void): Promise<any> {
        let profile: any = null;
        let earlyMM = false;
        const batch = { mm: null as any, at: null as any, history: null as any };
        this.send('ClearProfileCache', {});
        // One handler spans both commands, buffering early stats until the profile
        // and identity-bearing history can corroborate them. Never reuse a buffer.
        return this.request('GetProfile', { battleTag: player.name, clanTags: '', gatewayId }, (m, resolve, reject) => {
            if (m.messageType === 'UpdateProfileData') {
                const d = m.payload?.details;
                if (tag(d?.battle_tag_full) !== tag(player.name) || d?.requested_gateway_id !== gatewayId || !Number.isInteger(d?.matchmaked_current_season)) {
                    reject(new Error('Overlapping profile request')); return;
                }
                if (profile && JSON.stringify(profile) !== JSON.stringify(d)) {
                    reject(new Error('Profile changed during request')); return;
                }
                if (!profile) {
                    profile = d;
                    onProfile(d);
                    this.send('GetMatchHistory', { battleTag: player.name, gameMode: '1v1', arrangedTeams, gatewayId });
                }
            } else {
                const key = ({ MMStats: 'mm', ArrangedTeamStats: 'at', MatchHistoryUpdate: 'history' } as const)[m.messageType];
                if (!key) return;
                const value = key === 'history' ? m.payload?.matchHistory : m.payload?.message;
                if (!value || typeof value !== 'object' || Array.isArray(value) ||
                    (key === 'mm' && !Array.isArray(value.soloStats)) || (key === 'at' && !Array.isArray(value.gameTypes))) {
                    reject(new Error('Malformed stats batch')); return;
                }
                if (batch[key] && JSON.stringify(batch[key]) !== JSON.stringify(value)) {
                    reject(new Error('Conflicting stats batch')); return;
                }
                if (key === 'mm' && !profile && value.soloStats.length) earlyMM = true;
                batch[key] = value;
            }
            if (profile && batch.mm && batch.at && batch.history) {
                if (earlyMM && !Array.isArray(profile.matchmaked_stats)) reject(new Error('Early stats lack profile corroboration'));
                else resolve({ profile, batch });
            }
        }, 10000);
    }
    private async run(match: any, context: BnetMatchContext, generation: number): Promise<void> {
        const nativePlayers: any[] = Object.values(match.players || {}).map((p: any) => ({ ...p, name: nativeName(p.name) }));
        const players: any[] = nativePlayers.filter((p: any) => !p.isAI && p.team !== 24 && typeof p.name === 'string' && /^[^#\s]+#\d+$/.test(p.name)).slice(0, 24);
        const reported = new Set<string>();
        const fallback = new Map<string, { records: any[]; observedAt: number }>();
        const sentTimes = new Map<string, number>();
        const attempts = new Map<string, number>();
        const publish = (player: any, records: any[], source?: 'live' | 'cache', observedAt = Date.now()) => {
            if (!this.current(generation)) return;
            const fetchedAt = Math.max(Date.now(), (sentTimes.get(String(player.id)) || 0) + 1);
            sentTimes.set(String(player.id), fetchedAt);
            this.publish({ matchId: context.matchId, contextId: context.contextId, playerId: String(player.id), battleTag: player.name, fetchedAt, ...(source ? { source, observedAt } : {}), records: JSON.parse(JSON.stringify(records)) });
        };
        const finishUnavailable = (player: any) => {
            const id = String(player.id);
            if (!this.current(generation) || reported.has(id)) return;
            const entry = fallback.get(id);
            const age = entry ? Date.now() - entry.observedAt : Infinity;
            if (entry && age >= 0 && age <= CACHE_MAX_AGE_MS) {
                publish(player, entry.records, 'cache', entry.observedAt);
                this.report({ playerId: id, stage: 'player', attempt: attempts.get(id) || 0, outcome: 'cache',
                    reason: 'Fresh lookup exhausted', durationMs: 0, records: entry.records.length });
            } else publish(player, []);
            reported.add(id);
        };
        this.pruneCache();
        this.deadline = Date.now() + 90000;
        try {
            let gateways: Map<string, number>;
            for (let attempt = 0; attempt < 2; attempt++) {
                const started = Date.now();
                try {
                    if (attempt) { await this.delay(1000, generation); this.socket?.close(); }
                    await this.open(context, generation);
                    gateways = await this.request('GetGameInfo', {}, (m, resolve, reject) => {
                        if (m.messageType !== 'UpdateLoadingScreenInfo') return;
                        const data = m.payload?.gameData;
                        const comparison = compareBnetRoster(players, data);
                        const roster = Array.isArray(data?.teams) ? data.teams.flatMap(t => Array.isArray(t?.players) ? t.players : []) : [];
                        try { this.verbose({
                            kind: 'roster-comparison', attempt: attempt + 1, gameType: typeof data?.gameType === 'string' ? data.gameType.slice(0, 40) : '',
                            issues: comparison.issues, expectedCount: players.length, receivedCount: roster.length,
                            expected: nativePlayers.slice(0, 24).map(p => ({ slot: String(p.id).slice(0, 32), battleTag: p.name.slice(0, 128), eligible: players.includes(p) })),
                            received: roster.slice(0, 24).map(p => ({ slot: String(p?.playerId ?? '').slice(0, 32), battleTag: typeof p?.battletag === 'string' ? p.battletag.slice(0, 128) : '',
                                ...(typeof p?.gatewayId === 'number' || typeof p?.gatewayId === 'string' ? { gateway: String(p.gatewayId).slice(0, 16) } : {}) }))
                        }); } catch { /* Private diagnostics cannot interrupt stats. */ }
                        if (!comparison.gateways) reject(new Error('WC3 match roster mismatch'));
                        else resolve(comparison.gateways);
                    });
                    break;
                } catch (error) {
                    if (!this.current(generation)) return;
                    this.report({
                        stage: 'setup', attempt: attempt + 1, outcome: attempt ? 'unavailable' : 'retry',
                        reason: error?.message === 'WC3 match roster mismatch' ? 'WC3 match roster mismatch' : 'WC3 connection or roster timeout',
                        durationMs: Date.now() - started
                    });
                    if (attempt) return;
                }
            }
            let seasonInfo = this.seasonInfo;
            // Select possible fallbacks by exact roster/gateway, but never publish
            // them until fresh lookups have exhausted their retry budget.
            for (const player of players) {
                const key = this.cacheKey(player, gateways.get(String(player.id)));
                const entry = this.cache.get(key);
                if (!entry) continue;
                const age = Date.now() - entry.observedAt;
                if (age < 0 || age > CACHE_MAX_AGE_MS) { this.cache.delete(key); continue; }
                fallback.set(String(player.id), entry);
            }
            const leaderboards = new Map<string, any[]>();
            const rankJobs: { player: any; gatewayId: number; records: Map<string, any>; observedAt: number }[] = [];
            // Fair recovery rounds: a slow first player cannot consume all retries
            // before the other players get their initial lookup.
            for (let pass = 0; pass < 3; pass++) {
                if (pass) {
                    if (players.every(p => reported.has(String(p.id)))) break;
                    await this.delay(pass === 1 ? 1000 : 4000, generation);
                }
                for (const player of players) {
                    if (reported.has(String(player.id))) continue;
                    if (!this.current(generation)) return;
                    if (Date.now() >= this.deadline) throw new Error('Lookup budget exhausted');
                    attempts.set(String(player.id), pass + 1);
                    const records = new Map<string, any>();
                    const gatewayId = gateways.get(String(player.id));
                    const started = Date.now();
                    let completed = false;
                    const rejections = new Set<string>();
                    try {
                        // Separate subscriptions prevent callbacks on an old socket
                        // from entering the next player's collector. WC3 can still
                        // broadcast late events on the new connection; validate them.
                        if (pass || player !== players[0]) { this.socket?.close(); await this.open(context, generation); }
                        const { profile, batch } = await this.playerBatch(player, gatewayId, players.length > 2, freshProfile => {
                            if (seasonInfo && seasonInfo.seasonId !== freshProfile.matchmaked_current_season) { seasonInfo = null; this.seasonInfo = null; }
                            const cached = this.cache.get(this.cacheKey(player, gatewayId));
                            if (cached && cached.records.some(r => r.season !== freshProfile.matchmaked_current_season)) {
                                this.cache.delete(this.cacheKey(player, gatewayId));
                                fallback.delete(String(player.id));
                            }
                        });
                        const divisions = seasonInfo?.seasonId === profile.matchmaked_current_season ? seasonInfo.divisions : [];
                        for (const record of normalizeBnetStats(profile, batch, player.name, divisions, reason => rejections.add(reason))) records.set(JSON.stringify([record.season, record.gameMode, record.queue, record.race, record.team?.id]), record);
                        // A received batch is not necessarily a verified result. Retry
                        // rejected candidate stats, including temporarily empty history.
                        if (!records.size && (batch.mm.soloStats.length || batch.at.gameTypes.length ||
                            profile.matchmaked_stats?.some((s: any) => s.gateway_id === gatewayId && s.season_id === profile.matchmaked_current_season && (s.wins > 0 || s.losses > 0)))) {
                            throw new Error('Stats verification failed');
                        }
                        if (records.size > 128) throw new Error('Malformed stats batch');
                        completed = true;
                    } catch (error) {
                        if (!this.current(generation)) return;
                        const known = ['WC3 response timed out', 'WC3 disconnected', 'Socket unavailable', 'Socket send failed',
                            'Overlapping profile request', 'Profile changed during request', 'Conflicting stats batch',
                            'Malformed stats batch', 'Early stats lack profile corroboration', 'Stats verification failed', 'Malformed WC3 response', 'Attached WC3 has no available UI socket'];
                        const reason = known.includes(error?.message) ? error.message : 'WC3 lookup failed';
                        this.report({
                            playerId: String(player.id), stage: 'player', attempt: pass + 1,
                            outcome: pass < 2 ? 'retry' : 'unavailable', reason, durationMs: Date.now() - started, rejections: [...rejections]
                        });
                    }
                    if (!this.current(generation)) return;
                    if (!completed) { if (pass === 2) finishUnavailable(player); continue; }
                    // Publish verified MMR/record before optional badge/rank requests.
                    const observedAt = Date.now();
                    reported.add(String(player.id)); fallback.delete(String(player.id));
                    this.remember(player, gatewayId, [...records.values()], observedAt);
                    publish(player, [...records.values()], 'live', observedAt);
                    rankJobs.push({ player, gatewayId, records, observedAt });
                    this.report({
                        playerId: String(player.id), stage: 'player', attempt: pass + 1,
                        outcome: records.size ? 'ready' : 'empty', durationMs: Date.now() - started, records: records.size, rejections: [...rejections]
                    });
                }
            }
            // Optional rank requests also run only after all player lookups.
            for (const { player, gatewayId, records, observedAt } of rankJobs) {
                if (!this.current(generation) || Date.now() >= this.deadline) return;
                const beforeRank = JSON.stringify([...records.values()]);
                // The AT profile often reports rank zero even for rank one. Check
                // one bounded, cached leaderboard page; never scan every page.
                for (const record of records.values()) {
                    if (record.queue !== 'arranged' || record.rank !== undefined || !this.current(generation) || Date.now() >= this.deadline) continue;
                    const gameMode = record.gameMode + 'arranged';
                    const key = record.season + ':' + gameMode;
                    if (!leaderboards.has(key)) {
                        leaderboards.set(key, []);
                        try {
                            const rows = await this.request('GetLeaderboardData', {
                                gameMode, race: 'all', query: '', page: 1,
                                seasonId: record.season, filterByFriends: false
                            }, (m, resolve, reject) => {
                                if (m.messageType !== 'UpdateLeaderboardData') return;
                                const d = m.payload?.message;
                                if (d?.gameMode !== gameMode || d.seasonId !== record.season || d.currentpage !== 1 || d.hasError || !Array.isArray(d.rows)) reject(new Error('Leaderboard context mismatch'));
                                else resolve(d.rows.slice(0, 25));
                            });
                            if (!this.current(generation)) return;
                            leaderboards.set(key, rows);
                        } catch { /* Unknown rank remains absent. */ }
                    }
                    const roster = (members: any[]) => members.map(p => tag(p.battleTag) + ':' + p.gatewayId).sort().join('|');
                    const expected = roster(record.team.members);
                    const rows = leaderboards.get(key).filter(row => Array.isArray(row.players) && roster(row.players) === expected &&
                        row.mmr === record.mmr && row.wins === record.wins && row.losses === record.losses && Number.isInteger(row.rank) && row.rank > 0);
                    if (rows.length === 1) record.rank = rows[0].rank;
                }
                if (JSON.stringify([...records.values()]) !== beforeRank) {
                    this.remember(player, gatewayId, [...records.values()], observedAt);
                    publish(player, [...records.values()], 'live', observedAt);
                }
            }
            if (!this.current(generation) || Date.now() >= this.deadline) return;
            // League artwork is optional; never hold up the first MMR display.
            if (!seasonInfo || Date.now() - seasonInfo.fetchedAt > 600000) {
                try {
                    const info = await this.request('GetRankedSeasonStats', { gameMode: '1v1', race: 'random' }, (m, resolve) => {
                        if (m.messageType === 'RankedSeasonStatsUpdate' && Number.isInteger(m.payload?.seasonStats?.seasonId) &&
                            Array.isArray(m.payload.seasonStats.divisions)) resolve(m.payload.seasonStats);
                    });
                    if (!this.current(generation)) return;
                    seasonInfo = this.seasonInfo = { ...info, fetchedAt: Date.now() };
                    for (const player of players) {
                        if (!reported.has(String(player.id))) continue;
                        const entry = this.cache.get(this.cacheKey(player, gateways.get(String(player.id))));
                        if (!entry) continue;
                        let changed = false;
                        for (const record of entry.records) {
                            if (record.queue !== 'individual' || record.isPlaced === false || record.season !== info.seasonId) continue;
                            const division = info.divisions.find(d => record.mmr >= d.lowerBoundMMR && record.mmr < d.upperBoundMMR)?.divisionId;
                            if (Number.isInteger(division) && record.league !== division) { record.league = division; changed = true; }
                        }
                        if (changed) publish(player, JSON.parse(JSON.stringify(entry.records)), 'live', entry.observedAt);
                    }
                } catch { /* Optional metadata cannot erase a verified result. */ }
            }
        } catch {
            if (this.current(generation)) this.report({
                stage: 'setup', attempt: 1, outcome: 'unavailable',
                reason: Date.now() >= this.deadline ? 'Lookup budget exhausted' : 'WC3 setup failed', durationMs: 90000 - Math.max(0, this.deadline - Date.now())
            });
        } finally {
            for (const player of players) finishUnavailable(player);
            if (this.current(generation)) { this.pending = null; this.socket?.close(); this.socket = null; }
        }
    }
}
